/**
 * The off-the-edge behaviour of the provider rework (fb-msn0j0jv-3, decisions
 * of 10 Aug): the downloads always win where they cover, and online only fills
 * in beyond their edge, with a notice EACH TIME we cross it, because each
 * crossing is a fresh navigational fact and not a nag.
 *
 * Nothing renders. The runner watches the settled view bounds (MapView keeps
 * them fresh on every moveend) against the downloaded packs' coverage:
 *
 *   Online mode      stream whatever packs the view touches, silently, while
 *                    there is signal. The skipper asked for the water, not for
 *                    a list of regions to switch on one by one.
 *   Downloaded mode  downloaded packs draw as themselves (they always win —
 *                    streaming never replaces a saved chart, it only ADDS packs
 *                    that are not aboard). Crossing beyond the downloaded edge
 *                    streams the missing packs when online, and says so; with
 *                    no signal it says that instead, which at sea is the
 *                    difference between a surprise and a plan.
 *
 * The coverage test is boxCoveredBy's sample grid: advisory by design, and the
 * chart itself never lies, because what is not aboard is simply not drawn.
 */
import { useEffect, useRef } from 'react';
import { useChartSourcesStore } from '@/store/chartSources';
import { useChartPacksStore } from '@/store/chartPacks';
import { useNetworkStore } from '@/store/network';
import { useShellStore } from '@/store/shell';
import { useChartPlotterStore } from '@/store';
import { isVectorPack } from '@/chart-sources/providers';
import { packsIntersecting, boxCoveredBy, type LonLatBox } from '@/chart-sources/packSelection';

export function ChartEdgeRunner() {
  const viewBounds = useChartSourcesStore((s) => s.viewBounds);
  const packs = useChartPacksStore((s) => s.packs);
  const runtime = useChartPacksStore((s) => s.runtime);
  const mode = useChartPacksStore((s) => s.mode);
  const online = useNetworkStore((s) => s.online);
  const providerId = useChartPlotterStore((s) => s.chartProviderId);
  const showToast = useShellStore((s) => s.showToast);
  /** Whether the previous settled view was inside the downloaded coverage.
   *  null = no view judged yet, so opening the app beyond the edge counts as
   *  a crossing, which is what a skipper waking the phone mid-passage wants. */
  const prevCovered = useRef<boolean | null>(null);

  useEffect(() => {
    if (!viewBounds) return;
    // Only the pack-backed providers have an edge to fall off. The training
    // vector set and OpenSeaMap manage their own coverage story.
    const packKind =
      providerId === 'bsb' ? 'raster' : providerId === 'noaa-enc' || providerId === 'vector' ? 'vector' : null;
    if (!packKind) return;
    const relevant = packs.filter((p) => (packKind === 'vector') === isVectorPack(p));
    if (relevant.length === 0) return;
    const view = viewBounds as LonLatBox;

    if (mode === 'online') {
      // The whole provider is "leave it online": stream what the view touches.
      if (!online) return;
      for (const p of packsIntersecting(relevant, view)) {
        if ((runtime[p.id]?.status ?? 'idle') === 'idle') {
          void useChartPacksStore.getState().viewOnline(p);
        }
      }
      return;
    }

    // Downloaded mode. What is saved always draws (viewOnline never touches a
    // downloaded pack); the question is only what happens past the edge.
    const downloadedBoxes = relevant
      .filter((p) => runtime[p.id]?.status === 'downloaded')
      .map((p) => p.bounds as LonLatBox);
    if (downloadedBoxes.length === 0) {
      // Nothing aboard yet: there is no "edge" to speak of, and nagging about
      // a boundary that does not exist teaches the notice to be ignored.
      prevCovered.current = null;
      return;
    }
    const covered = boxCoveredBy(view, downloadedBoxes);
    if (!covered) {
      const needed = packsIntersecting(relevant, view).filter(
        (p) => (runtime[p.id]?.status ?? 'idle') === 'idle',
      );
      if (online) {
        for (const p of needed) void useChartPacksStore.getState().viewOnline(p);
      }
      if (prevCovered.current !== false) {
        showToast(
          online
            ? 'Beyond your downloaded area — streaming these charts online'
            : 'Beyond your downloaded area — no signal, so the gap stays blank',
        );
      }
    }
    prevCovered.current = covered;
  }, [viewBounds, packs, runtime, mode, online, providerId, showToast]);

  return null;
}
